import React, { useEffect, useCallback, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import moment from "moment";
import { IDataTableRow } from "../../../../components/DataTable/DataTableBody/DataTableBody";
import useAppContext from "../../../../context/hooks/useAppContext";
import { AssignedParkingRight } from "../../../../models/delegations/DelegatedParkingProductsModel";
import useMyProductsContext from "../../../../context/hooks/useMyProductsContext";
import useFleetManagerContext from "../../../../context/hooks/useFleetManagerContext";
import { ForFleetManager } from "../../../../models/ForFleetManager";
import { WithFiltersAndPaging } from "../../../../models/filters/WithFiltersAndPaging";
import {
  DataTableContextFilters,
  useDataTableContext,
} from "../../../../components/DataTable/DataTableContext/DataTableContextProvider";
import filteredColumnObjectBuilder from "../ObjectBuilders/FilteredColumnObjectBuilder";
import { FmpFilters } from "../../../../constants/filtering.constants";
import { FilterType } from "../../../../components/DataTable/DataTableFilters/DataTableFilters";
import {
  convertToRemainingFlex,
  NumberPlate,
  ProductChip,
  selectEnableFleetVehicleScheduling,
  selectShowActiveSession,
  useCurrentlySelectedParkingProduct,
  useGetOccupiedParkingRightsCount,
  useProductOptions,
  useSharedProductsMenuAction,
} from "./common";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { isNil } from "lodash";
import DataTable from "../../../../components/DataTable/DataTable";
import TruncatedText from "../../../../components/TruncatedText/TruncatedText";
import {
  DeleteDelegeeIcon,
  DeleteDelegeeMenuItem,
  EditIcon,
  NoPaddingGrid,
} from "../../FleetManagerDataTable/StyledComponents";
import { Column } from "../../../../components/DataTable/DataTableHead/DataTableHead";
import { MenuItem } from "@material-ui/core";
import { stringifyDVARowKey } from "../../../../helpers/vehicleAssignment.utils";
import { ForbiddenIconMenu } from "../../../../ui/Icons/ForbiddenIcon";
import { ParkingProductType } from "../../../../models/ParkingProductModel";
import { getVehiclesThunk } from "../../../../store/actions/parkingProducts.thunks";
import { convertFiltersToGetVehicleAssignmentsQuery } from "../../../../services/parkingProducts.service";

const Wrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;

export interface AssignedVehicleRow extends IDataTableRow {
  numberPlate: JSX.Element;
  description?: JSX.Element;
  product: JSX.Element;
  assignedDate: string;
  startedDate: string | null;
  activationDate: string | null;
  expirationDate: string | null;
  remainingFlex: string;
  activeSessionSince?: string | null;
}
export type VehicleAssignmentsTableProps = Readonly<{
  onRevokeClicked: () => void;
  onDeleteClicked: () => void;
  subHeading?: ReactNode;
}>

const _createData = (vehicleAssignment: AssignedParkingRight, showFlex?: boolean, showScheduling?: boolean, showActiveSession?: boolean): AssignedVehicleRow => {
  let data = {
    rowKey: stringifyDVARowKey({
      vehicleAssignmentId: vehicleAssignment.vehicleAssignmentId,
      pmcIds: [vehicleAssignment.parkingRight.pmc],
    }),
    numberPlate: (
      <NumberPlate
        countryCode={vehicleAssignment.vehicle.countryCode}
        identifier={vehicleAssignment.vehicle.identifier}
      />
    ),
    description: <TruncatedText text={vehicleAssignment.vehicle.description} />,
    product: (
      <Wrapper>
        <ProductChip
          name={vehicleAssignment.parkingRight.name}
          location={vehicleAssignment.parkingRight.location}
        />
      </Wrapper>
    ),
    assignedDate: moment(vehicleAssignment.assigned).format("DD/MM/YYYY"),
    ...(!showScheduling && {
      startedDate: vehicleAssignment.startedAt ? moment.utc(vehicleAssignment.startedAt).format("DD/MM/YYYY") : null,
    }),
    ...(showScheduling && {
      activationDate: vehicleAssignment.activationDate ? moment.parseZone(vehicleAssignment.activationDate).format("DD/MM/YYYY") : null,
      expirationDate: vehicleAssignment.expirationDate ? moment.parseZone(vehicleAssignment.expirationDate).format("DD/MM/YYYY") : null,
    }),
    ...(showActiveSession && {
      activeSessionSince: vehicleAssignment.activeSessionSince ? moment.parseZone(vehicleAssignment.activeSessionSince).format("DD/MM/YYYY HH:mm") : null,
    }),
  } as any;

  if (showFlex) {
    data = {
      ...data,
      remainingFlex: convertToRemainingFlex(vehicleAssignment.flexBudgetRemainingTime, vehicleAssignment.flexBudgetUnitType)
    }
  }
  return data;
};

type MenuItemsProps = Readonly<{
  onDelete: () => void;
  onEdit: () => void;
  onRevoke: () => void;
}>;

function MenuItems(props: MenuItemsProps) {
  const { t } = useTranslation("fleetManagerTable");

  return (
    <>
      <MenuItem key="edit" onClick={() => props.onEdit()}>
        <EditIcon />
        {t("contextMenuActions.editVehicle")}
      </MenuItem>
      <MenuItem key="revoke" onClick={() => props.onRevoke()}>
        <ForbiddenIconMenu />
        {t("contextMenuActions.revokeVehicle")}
      </MenuItem>
      <DeleteDelegeeMenuItem key="delete" onClick={() => props.onDelete()}>
        <DeleteDelegeeIcon />
        {t("contextMenuActions.deleteVehicle")}
      </DeleteDelegeeMenuItem>
    </>
  );
}

function VehiclesTable(props: VehicleAssignmentsTableProps) {
  const dispatch = useAppDispatch();
  const vehicleAssignments = useAppSelector((state) => state.parkingProducts.vehiclesMyProducts);
  const getVehicleAssignments = useCallback(
    (request: WithFiltersAndPaging<ForFleetManager>) => {
      return dispatch(getVehiclesThunk(convertFiltersToGetVehicleAssignmentsQuery(request)));
    },
    [dispatch]
  );
  const { t } = useTranslation(["myProducts", "globals", "dataTable", "fleetManagerTable"]);
  const locatedParkingProducts = useAppSelector((state) => state.parkingProducts.locatedParkingProducts);

  const { myProductsState, setSorting } = useMyProductsContext();
  const {
    fleetManagerState,
    setSelectedParkingRightIds,
    toggleEditVehicleAssignment,
    toggleDeleteVehicleAssignmentsWarning,
    toggleRevokeVehicleAssignmentsWarning
  } = useFleetManagerContext();

  const dataTableContext = useDataTableContext();

  const { appState } = useAppContext();

  const getTotalOccupiedParkingRights = useGetOccupiedParkingRightsCount();

  const selectedLocatedProduct = useCurrentlySelectedParkingProduct();

  const hasFlexBudgedSeasonTickets = locatedParkingProducts.data?.some(p => p.hasFlexBudgedSeasonTickets);

  const getFilters = (): DataTableContextFilters[] => [
    {
      key: FmpFilters.placeId,
      type: FilterType.QueryParam,
      value: myProductsState.selectedLocation.placeId.toString(),
    },
    {
      key: FmpFilters.sortBy,
      type: FilterType.QueryParam,
      value: myProductsState.dataTable.sortBy,
    },
    {
      key: FmpFilters.orderDesc,
      type: FilterType.QueryParam,
      value: myProductsState.dataTable.orderDesc?.toString(),
    },
    ...(dataTableContext.state.filters as DataTableContextFilters[]),
  ];

  const getRequestModel = (): WithFiltersAndPaging<ForFleetManager> => {
    const request: WithFiltersAndPaging<ForFleetManager> = {
      entity: {
        seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
        language: appState.selectedLanguage,
      },
      pagination: {
        pageSize: dataTableContext.state.pageSize!,
        pageNumber: dataTableContext.state.pageNumber!,
      },
      filters: getFilters(),
    };

    return request;
  };

  const [fetchKey, setFetchKey] = useState(0);

  const fetchVehicleAssignments = () => {
    if (appState.user.seasonTicketOwnerCrmId) {
      return getVehicleAssignments(getRequestModel());
    }
  };

  const memoizedFetch = useCallback(
    () => fetchVehicleAssignments(), // eslint-disable-next-line
    [
      dataTableContext.state.pageSize,
      dataTableContext.state.pageNumber,
      myProductsState.dataTable.orderDesc,
      myProductsState.dataTable.sortBy,
      myProductsState.selectedLocation.placeId,
      appState.user.seasonTicketOwnerCrmId,
      appState.selectedLanguage,
      fetchKey
    ]
  );

  useEffect(() => {
    const req = memoizedFetch();
    return () => req?.abort();
  }, [memoizedFetch]);

  const productTableOptions = useProductOptions({ productType: ParkingProductType.ParkingRights });
  const sharedProductActions = useSharedProductsMenuAction();
  var hasUnlimitedContracts = selectedLocatedProduct?.parkingProducts.find(p => p.hasUnlimitedEntryRights === true) !== undefined;
  const enableFleetVehicleScheduling = useAppSelector(selectEnableFleetVehicleScheduling) && !hasUnlimitedContracts;
  const showActiveSession = useAppSelector(selectShowActiveSession);

  const columns: Column<AssignedVehicleRow>[] = filteredColumnObjectBuilder.buildVehiclesMyProductsColumns(
    t,
    productTableOptions,
    hasFlexBudgedSeasonTickets,
    enableFleetVehicleScheduling,
    showActiveSession
  );

  const onSort = (event: React.MouseEvent<unknown>, property: keyof AssignedVehicleRow) => {
    setSorting(property.toString());
  };

  const getTableRows = (): AssignedVehicleRow[] => {
    const { pages } = vehicleAssignments;
    if (isNil(pages)) return [];
    const pageData = pages[dataTableContext.state.pageNumber!]?.data;
    if (isNil(pageData)) return [];
    return pageData.map(vehicles => _createData(vehicles, hasFlexBudgedSeasonTickets, enableFleetVehicleScheduling, showActiveSession));
  };

  const isLoading =
    vehicleAssignments.pages?.[dataTableContext.state.pageNumber!]?.loading ?? false;
  const rows = getTableRows();

  const handleEdit = (row: AssignedVehicleRow | null) => {
    if (isNil(row)) {
      return;
    }
    toggleEditVehicleAssignment(row);
  };

  const handleDelete = (row: AssignedVehicleRow | null) => {
    if (isNil(row)) {
      return;
    }
    toggleDeleteVehicleAssignmentsWarning(row);
  };

  const handleRevoke = (row: AssignedVehicleRow | null) => {
    if (isNil(row)) {
      return;
    }
    toggleRevokeVehicleAssignmentsWarning(row);
  };

  return (
    <NoPaddingGrid container item direction="row" xs={12}>
      <NoPaddingGrid container item direction="column" xs={12}>
        <DataTable
          rows={rows}
          columns={columns}
          key={myProductsState.selectedLocation.placeId}
          loading={isLoading}
          orderBy={myProductsState.dataTable.sortBy ?? ""}
          pages={vehicleAssignments.totalPages}
          onFilterClicked={() => setFetchKey(prev => prev + 1)}
          onResetFiltersClicked={() => setFetchKey(prev => prev + 1)}
          onRequestSort={onSort}
          translationOverrideKey={"vehicles"}
          selectedRowKeys={fleetManagerState.selectedRowKeys}
          setSelectedRowKeys={setSelectedParkingRightIds}
          renderActionMenu={(focusedRow, onClose) => (
            <MenuItems
              onDelete={() => handleDelete(focusedRow)}
              onEdit={() => handleEdit(focusedRow)}
              onRevoke={() => handleRevoke(focusedRow)}
            />
          )}
          order={myProductsState.dataTable.orderDesc ? "desc" : "asc"}
          heading={{
            options: {
              useHeading: true,
              showHeadingCount: true,
            },
            title: t("globals:parkingRights"),
            count: getTotalOccupiedParkingRights(selectedLocatedProduct || {}),
          }}
          filtering={{
            options: {
              useFilters: true,
            },
          }}
          actions={{
            options: {
              useActions: true,
            },
            revoke: {
              useRevoke: true,
              showRevoke: fleetManagerState.selectedRowKeys.length > 0,
              revokeLabel: `${enableFleetVehicleScheduling ?
                (fleetManagerState.selectedRowKeys.length === 1 ? t("globals:revokeProduct") : t("globals:revokeProducts"))
              : t("globals:revoke")} (${fleetManagerState.selectedRowKeys.length})`,
              onRevokeClicked: props.onRevokeClicked,
            },
            menu: sharedProductActions.menu,
          }}
          subHeading={props.subHeading}
        />
      </NoPaddingGrid>
    </NoPaddingGrid>
  );
}

export default VehiclesTable;
